import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api, getStoredToken, setStoredToken, apiPublic } from '../lib/api';
import { registerPushNotifications } from '../lib/pushNotifications';
import { signInWithAppleNative } from '../lib/appleSignIn';
import { getOrCreateAnalyticsSession } from '../lib/influencerTracking';

export type AuthUser = {
  id: string;
  email: string;
  displayName: string | null;
  preferredUnitSystem: string;
  preferredTheme: string | null;
  onboardingCompleted: boolean;
  coachAccessUnlocked: boolean;
  aiCoachingEnabled: boolean;
  trackNutrition: boolean;
  trackWorkouts: boolean;
  trackSleep: boolean;
  trackGoals: boolean;
  adFreeEnabled: boolean;
  circlesEnabled: boolean;
  leaderboardsOptIn: boolean;
};

type MeResponse = AuthUser & {
  profile: unknown;
  createdAt?: string;
  trackNutrition?: boolean;
  trackWorkouts?: boolean;
  trackSleep?: boolean;
  trackGoals?: boolean;
  adFreeEnabled?: boolean;
};

type Ctx = {
  user: AuthUser | null;
  profile: MeResponse['profile'] | null;
  token: string | null;
  loading: boolean;
  refresh: () => Promise<void>;
  login: (email: string, password: string) => Promise<AuthUser>;
  loginWithApple: () => Promise<AuthUser>;
  signup: (email: string, password: string, displayName?: string) => Promise<{ earlyBirdCode?: string | null }>;
  logout: () => void;
  setUserFields: (p: Partial<AuthUser>) => void;
};

const AuthContext = createContext<Ctx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => getStoredToken());
  const [user, setUser] = useState<AuthUser | null>(null);
  const [profile, setProfile] = useState<MeResponse['profile'] | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const t = getStoredToken();
    setToken(t);
    if (!t) {
      setUser(null);
      setProfile(null);
      setLoading(false);
      return;
    }
    try {
      const me = await api<MeResponse>('/api/auth/me');
      setUser({
        id: me.id,
        email: me.email,
        displayName: me.displayName,
        preferredUnitSystem: me.preferredUnitSystem,
        preferredTheme: me.preferredTheme ?? null,
        onboardingCompleted: me.onboardingCompleted,
        coachAccessUnlocked: Boolean(me.coachAccessUnlocked),
        aiCoachingEnabled: Boolean(me.aiCoachingEnabled),
        trackNutrition: me.trackNutrition !== false,
        trackWorkouts: me.trackWorkouts !== false,
        trackSleep: me.trackSleep !== false,
        trackGoals: me.trackGoals !== false,
        adFreeEnabled: Boolean(me.adFreeEnabled),
        circlesEnabled: Boolean(me.circlesEnabled),
        leaderboardsOptIn: Boolean(me.leaderboardsOptIn),
      });
      setProfile(me.profile ?? null);
    } catch {
      setStoredToken(null);
      setToken(null);
      setUser(null);
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (user?.id) void registerPushNotifications(user.id);
  }, [user?.id]);

  async function applySession(res: { token: string; user: AuthUser }) {
    setStoredToken(res.token);
    setToken(res.token);
    setProfile(null);
    const me = await api<MeResponse>('/api/auth/me');
    setUser({
      id: me.id,
      email: me.email,
      displayName: me.displayName,
      preferredUnitSystem: me.preferredUnitSystem,
      preferredTheme: me.preferredTheme ?? null,
      onboardingCompleted: me.onboardingCompleted,
      coachAccessUnlocked: Boolean(me.coachAccessUnlocked),
      aiCoachingEnabled: Boolean(me.aiCoachingEnabled),
      trackNutrition: me.trackNutrition !== false,
      trackWorkouts: me.trackWorkouts !== false,
      trackSleep: me.trackSleep !== false,
      trackGoals: me.trackGoals !== false,
      adFreeEnabled: Boolean(me.adFreeEnabled),
      circlesEnabled: Boolean(me.circlesEnabled),
      leaderboardsOptIn: Boolean(me.leaderboardsOptIn),
    });
    setProfile(me.profile ?? null);
  }

  const login = useCallback(async (email: string, password: string) => {
    const res = await apiPublic<{ token: string; user: AuthUser }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    await applySession(res);
    return res.user;
  }, []);

  const loginWithApple = useCallback(async () => {
    const apple = await signInWithAppleNative();
    const res = await apiPublic<{ token: string; user: AuthUser }>('/api/auth/apple', {
      method: 'POST',
      body: JSON.stringify({
        identityToken: apple.identityToken,
        givenName: apple.givenName,
        familyName: apple.familyName,
      }),
    });
    await applySession(res);
    return res.user;
  }, []);

  const signup = useCallback(async (email: string, password: string, displayName?: string) => {
    let ref: string | undefined;
    let sessionKey: string | undefined;
    try {
      ref = localStorage.getItem('ppiq_ref') ?? undefined;
      sessionKey = getOrCreateAnalyticsSession();
    } catch {
      /* ignore */
    }
    const res = await apiPublic<{
      token: string;
      user: AuthUser;
      earlyBirdCode?: string | null;
    }>('/api/auth/signup', {
      method: 'POST',
      body: JSON.stringify({ email, password, displayName, ref, sessionKey }),
    });
    await applySession(res);
    return { earlyBirdCode: res.earlyBirdCode ?? null };
  }, []);

  const logout = useCallback(() => {
    setStoredToken(null);
    setToken(null);
    setUser(null);
    setProfile(null);
  }, []);

  const setUserFields = useCallback((p: Partial<AuthUser>) => {
    setUser((u) => (u ? { ...u, ...p } : null));
  }, []);

  const value = useMemo(
    () => ({
      user,
      profile,
      token,
      loading,
      refresh,
      login,
      loginWithApple,
      signup,
      logout,
      setUserFields,
    }),
    [user, profile, token, loading, refresh, login, loginWithApple, signup, logout, setUserFields]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const c = useContext(AuthContext);
  if (!c) throw new Error('useAuth outside AuthProvider');
  return c;
}
